import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { range } from 'lodash';
import { minToHoursInput } from './utils';
import {
  hoursToMinutes,
  minutesToHoursAndMinutes,
} from '../../../../../../../utils/time';

// One shared duration input so every project time field behaves the same
// regardless of browser/OS locale. The native `type="time"` it replaced
// rendered as a 12h "12:00 AM" clock on some machines, making a duration look
// like a time of day. This shows a single compact `HH:MM` trigger that opens a
// popover with two scrollable columns (hours, minutes). It is always 24h with
// no AM/PM, one tap target on mobile, and the duration can exceed 24h.
//
// The open/close state is owned here with a plain outside-click + Escape
// handler rather than react-bootstrap's `Dropdown`: its auto-close treats
// clicks on our custom column buttons as "inside" and the controlled variant
// closed unreliably across many instances in a grid.
type Props = {
  hours: number | null;
  disabled: boolean;
  compactMode: boolean;
  onChange: (hours: number | null) => void;
};

const MAX_HOURS = 23;

const pad = (value: number) => value.toString().padStart(2, '0');

const HoursInput: React.FC<Props> = ({
  hours,
  disabled,
  compactMode,
  onChange,
}) => {
  const totalMinutes = hours == null ? 0 : Math.round(hoursToMinutes(hours));
  const [selectedHours, selectedMinutes] =
    minutesToHoursAndMinutes(totalMinutes);

  // High-water mark so the hours column never shrinks: a long duration that has
  // been shown stays selectable even after the value is lowered.
  const maxHourRef = useRef(MAX_HOURS);
  maxHourRef.current = Math.max(maxHourRef.current, selectedHours);
  const maxHourOption = maxHourRef.current;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on any click outside this picker or on Escape. Listening on the
  // capture phase guarantees we see the event before anything can stop it.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  // Picks commit live but leave the popover open so both columns can be set in
  // one go; it closes on an outside click, Escape, or the trigger.
  const emit = useCallback(
    (h: number, m: number) => {
      const total = h * 60 + m;
      // A zero duration is treated as no entry, like an empty field.
      onChange(total === 0 ? null : minToHoursInput(total));
    },
    [onChange],
  );

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        aria-label="duration"
        aria-expanded={open}
        disabled={disabled}
        $compact={compactMode}
        onClick={() => setOpen((prev) => !prev)}>
        <span>
          {pad(selectedHours)}:{pad(selectedMinutes)}
        </span>
        <Caret aria-hidden>▾</Caret>
      </Trigger>
      {open && (
        <Menu>
          <PickerColumn
            unit="hours"
            count={maxHourOption + 1}
            selected={selectedHours}
            onPick={(h) => emit(h, selectedMinutes)}
          />
          <Separator>:</Separator>
          <PickerColumn
            unit="minutes"
            count={60}
            selected={selectedMinutes}
            onPick={(m) => emit(selectedHours, m)}
          />
        </Menu>
      )}
    </Root>
  );
};

export default HoursInput;

// One scrollable column of zero-padded values; shared by hours and minutes.
const PickerColumn: React.FC<{
  unit: 'hours' | 'minutes';
  count: number;
  selected: number;
  onPick: (value: number) => void;
}> = ({ unit, count, selected, onPick }) => (
  <Column aria-label={unit}>
    {range(count).map((value) => (
      <Item
        key={value}
        type="button"
        aria-label={`${value} ${unit}`}
        $active={value === selected}
        onClick={() => onPick(value)}>
        {pad(value)}
      </Item>
    ))}
  </Column>
);

const Root = styled.div({
  position: 'relative',
  width: '100%',
});

// A button styled to read like a Bootstrap form control.
const Trigger = styled.button<{ $compact: boolean }>(({ $compact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  width: '100%',
  backgroundColor: 'var(--bs-body-bg)',
  color: 'var(--bs-body-color)',
  border: '1px solid var(--bs-border-color)',
  borderRadius: 'var(--bs-border-radius)',
  padding: $compact ? '0.25rem 0.5rem' : '0.375rem 0.75rem',
  fontSize: $compact ? '0.875rem' : undefined,
  fontVariantNumeric: 'tabular-nums',
  cursor: 'pointer',
  '&:hover, &:focus': {
    borderColor: 'var(--bs-primary)',
  },
  '&:disabled': {
    backgroundColor: 'var(--bs-body-bg)',
    cursor: 'default',
    opacity: 0.65,
  },
}));

const Caret = styled.span({
  fontSize: '0.7rem',
  lineHeight: 1,
  color: 'var(--bs-secondary-color)',
});

const Menu = styled.div({
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 1000,
  marginTop: '0.125rem',
  display: 'flex',
  padding: '0.25rem',
  backgroundColor: 'var(--bs-body-bg)',
  border: '1px solid var(--bs-border-color)',
  borderRadius: 'var(--bs-border-radius)',
  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
});

const Column = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '3rem',
  maxHeight: '12rem',
  overflowY: 'auto',
  // Thin scrollbar so the column stays narrow.
  scrollbarWidth: 'thin',
});

const Separator = styled.span({
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.25rem',
  color: 'var(--bs-secondary-color)',
});

const Item = styled.button<{ $active: boolean }>(({ $active }) => ({
  border: 'none',
  background: $active ? 'var(--bs-primary)' : 'transparent',
  color: $active ? 'var(--bs-white)' : 'var(--bs-body-color)',
  borderRadius: '0.25rem',
  padding: '0.25rem 0.75rem',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  cursor: 'pointer',
  '&:hover': {
    background: $active ? 'var(--bs-primary)' : 'var(--bs-tertiary-bg)',
  },
}));
